import React, { useMemo, useRef, useState } from "react";
import { Input } from "@/components/ui/input";

/**
 * Typeahead untuk pemilihan truk dari master.
 * Ketik plate/supir/transporter → filter list realtime → klik untuk pilih.
 *
 * Props:
 *   trucks:      array dari master truk aktif
 *   selectedId:  string | ""
 *   onSelect:    (truckId) => void
 */
// Canonicalize plate/text for comparison — lowercase + strip non-alphanumeric.
// This makes "BM 1234 XX", "bm1234xx", and "BM-1234-XX" all match the same haystack.
const _canon = (s) => String(s || "").toLowerCase().replace(/[^a-z0-9]/g, "");

export default function TruckTypeahead({ trucks, selectedId, onSelect }) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const blurTimerRef = useRef(null);

  const selected = trucks.find((t) => t.id === selectedId);

  const matches = useMemo(() => {
    const needleRaw = query.trim();
    if (!needleRaw) return trucks.slice(0, 30);
    // 1) Try substring on the canonicalized haystack (matches "BM1234XX" against "BM-1234 XX")
    const needleCanon = _canon(needleRaw);
    // 2) Also allow substring on the raw lowercase haystack (matches supir/transporter names with spaces)
    const needleLower = needleRaw.toLowerCase();
    return trucks
      .filter((t) => {
        const hayRaw = `${t.no_polisi || ""} ${t.transporter || ""} ${t.supir || ""}`.toLowerCase();
        const hayCanon = _canon(hayRaw);
        return hayCanon.includes(needleCanon) || hayRaw.includes(needleLower);
      })
      .slice(0, 30);
  }, [trucks, query]);

  const pick = (t) => {
    onSelect(t.id);
    setQuery("");
    setOpen(false);
  };

  const clearSelection = () => {
    onSelect("");
    setQuery("");
    setOpen(true);
  };

  const onKeyDown = (e) => {
    if (!open) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActiveIdx((i) => Math.min(matches.length - 1, i + 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActiveIdx((i) => Math.max(0, i - 1)); }
    else if (e.key === "Enter" && matches[activeIdx]) { e.preventDefault(); pick(matches[activeIdx]); }
    else if (e.key === "Escape") { setOpen(false); }
  };

  // Delay blur so onMouseDown on list items fires before we close
  const onBlur = () => {
    blurTimerRef.current = setTimeout(() => setOpen(false), 150);
  };
  const onFocus = () => {
    if (blurTimerRef.current) clearTimeout(blurTimerRef.current);
    setOpen(true);
    setActiveIdx(0);
  };

  return (
    <div className="relative" data-testid="truck-typeahead">
      <Input
        value={query || (selected ? `${selected.no_polisi} · ${selected.transporter || ""}${selected.supir ? " · " + selected.supir : ""}` : "")}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); setActiveIdx(0); }}
        onFocus={onFocus}
        onBlur={onBlur}
        onKeyDown={onKeyDown}
        placeholder="Ketik plat / transporter / supir..."
        className="rounded-none h-11 font-mono"
        data-testid="truck-typeahead-input"
        autoComplete="off"
      />
      {selected && !open && (
        <button
          type="button"
          onMouseDown={(e) => { e.preventDefault(); clearSelection(); }}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground hover:text-destructive font-mono"
          data-testid="truck-typeahead-clear"
          aria-label="Hapus pilihan truk"
        >
          ✕
        </button>
      )}
      {open && (
        <div
          className="absolute z-40 left-0 right-0 top-full mt-1 border border-border bg-card max-h-72 overflow-y-auto shadow-lg"
          data-testid="truck-typeahead-list"
        >
          {matches.length === 0 ? (
            <div className="p-3 text-xs text-muted-foreground">
              Tidak ada truk cocok. Coba kata kunci lain atau pakai <strong>Input Manual</strong>.
            </div>
          ) : (
            matches.map((t, i) => (
              <button
                key={t.id}
                type="button"
                onMouseDown={(e) => { e.preventDefault(); pick(t); }}
                className={`w-full text-left px-3 py-2 border-b border-border last:border-b-0 font-mono text-sm ${
                  i === activeIdx ? "bg-primary/10" : "hover:bg-secondary/40"
                } ${selectedId === t.id ? "font-bold" : ""}`}
                data-testid={`truck-typeahead-option-${t.no_polisi}`}
              >
                <div className="flex items-center gap-2">
                  <span className="font-bold">{t.no_polisi}</span>
                  <span className="text-muted-foreground text-xs">·</span>
                  <span className="text-xs">{t.transporter || "-"}</span>
                  {t.supir && (
                    <>
                      <span className="text-muted-foreground text-xs">·</span>
                      <span className="text-xs text-muted-foreground">{t.supir}</span>
                    </>
                  )}
                </div>
              </button>
            ))
          )}
          <div className="px-3 py-1.5 border-t border-border text-[10px] text-muted-foreground bg-secondary/30">
            {matches.length} dari {trucks.length} truk · Enter untuk pilih · Esc untuk tutup
          </div>
        </div>
      )}
    </div>
  );
}
